import React from 'react';
import { palette, typography, dimensions } from '../theme';

type ButtonVariant = 'primary' | 'secondary' | 'outline';
type ButtonSize = 'XS' | 'S' | 'L';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}

const HEIGHTS: Record<ButtonSize, number> = {
  XS: dimensions.controlHeight.xs,
  S: dimensions.controlHeight.sm,
  L: dimensions.controlHeight.lg
};
const FONT_SIZES: Record<ButtonSize, number> = { XS: 12, S: 14, L: 14 };
const PADDINGS: Record<ButtonSize, string> = { XS: '4px 12px', S: '8px 16px', L: '8px 16px' };

const colorsFor = (variant: ButtonVariant, disabled: boolean) => {
  if (disabled) {
    return {
      background: variant === 'outline' ? palette.primitives.base.white : palette.primitives.grey.grey50,
      border: palette.primitives.grey.grey50,
      color: palette.primitives.grey.grey300
    };
  }
  if (variant === 'primary') {
    return {
      background: palette.primitives.purple.purple400,
      border: palette.primitives.purple.purple100,
      color: palette.primitives.base.white
    };
  }
  if (variant === 'outline') {
    return {
      background: palette.primitives.base.white,
      border: palette.primitives.purple.purple200,
      color: palette.primitives.purple.purple400
    };
  }
  return {
    background: palette.primitives.purple.purple50,
    border: palette.primitives.purple.purple200,
    color: palette.primitives.purple.purple400
  };
};

export const ActionButton: React.FC<ButtonProps> = ({
  children,
  style,
  variant = 'secondary',
  size = 'S',
  fullWidth,
  disabled,
  ...rest
}) => {
  const c = colorsFor(variant, !!disabled);
  return (
    <button
      {...rest}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: fullWidth ? '100%' : undefined,
        height: HEIGHTS[size],
        padding: PADDINGS[size],
        borderRadius: 8,
        border: `1px solid ${c.border}`,
        backgroundColor: c.background,
        color: c.color,
        fontFamily: typography.fontFamily.ui,
        fontSize: FONT_SIZES[size],
        fontWeight: typography.fontWeight.semibold,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 120ms ease',
        ...style
      }}
    >
      {children}
    </button>
  );
};
